import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, Space, Table, Tag, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

export const fmtBytes = (v) => {
    const n = Number(v) || 0;
    if (n >= 1024 ** 4) return `${(n / 1024 ** 4).toFixed(2)} TB`;
    if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
    if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${n} B`;
};
export const fmtTime = (s) => (s ? String(s).replace('T', ' ').slice(0, 19) : '-');
export const fmtNum = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString());

/** apiClient 已对 HTTP 4xx(除 404)/5xx 弹过提示；这里只补 404 和非 HTTP 错误 */
export const toastErr = (e, fallback) => {
    if (!e?.status || e.status === 404) message.error(e?.error || e?.message || fallback);
};

const STATUS = {
    active: ['green', '正常'], disabled: ['default', '已停用'], suspended: ['orange', '已暂停'],
    deleted: ['red', '已删除'], revoked: ['red', '已撤销'], expired: ['default', '已过期'],
};
export const StatusTag = ({ status }) => {
    const [color, text] = STATUS[status] || ['default', status || '-'];
    return <Tag color={color} style={{ marginInlineEnd: 0 }}>{text}</Tag>;
};

const ROLE = { personal: '个人', owner: '组织所有者', admin: '组织管理员', member: '组织成员' };
export const RoleTag = ({ role }) => <Tag style={{ marginInlineEnd: 0 }}>{ROLE[role] || role || '-'}</Tag>;
export const ModeTag = ({ mode }) => <Tag style={{ marginInlineEnd: 0 }}>{mode === 'seat' ? '按席位' : mode === 'pool' ? '共享空间' : (mode || '-')}</Tag>;

/** 列表结果容错：数组 / {items,total} / {data,total} */
const parseList = (r) => {
    const d = r?.data;
    if (Array.isArray(d)) return { items: d, total: null };
    const items = d?.items || d?.data || d?.list || [];
    return { items: Array.isArray(items) ? items : [], total: d?.total ?? d?.pagination?.total ?? null };
};

/**
 * 通用搜索 + 分页列表。fetcher(params) → 后端 body。reloadKey 变化时重新加载。
 * 后端返回 total 时走服务端分页（page / per_page），否则前端分页。
 */
export const SearchTable = ({ title, fetcher, columns, placeholder = '搜索', rowKey = 'id', reloadKey, extraFilters, filters = {}, scrollX = 1000 }) => {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [data, setData] = useState({ items: [], total: null });
    const [loading, setLoading] = useState(false);
    const filtersKey = JSON.stringify(filters);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await fetcher({ q: q || undefined, page, per_page: pageSize, ...JSON.parse(filtersKey) });
            setData(parseList(r));
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [fetcher, q, page, pageSize, filtersKey]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const server = data.total !== null && data.total !== undefined;
    return (
        <Card title={title} extra={<Space wrap>
            {extraFilters}
            <Input.Search allowClear placeholder={placeholder} style={{ width: 260 }}
                onSearch={(v) => { setPage(1); setQ(v.trim()); }} />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
        </Space>}>
            <Table rowKey={rowKey} size="middle" columns={columns} dataSource={data.items} loading={loading} scroll={{ x: scrollX }}
                pagination={server
                    ? { current: page, pageSize, total: data.total, showSizeChanger: true, showTotal: (n) => `共 ${n} 条`, onChange: (p, s) => { setPage(p); setPageSize(s); } }
                    : { pageSize: 20, showSizeChanger: true, showTotal: (n) => `共 ${n} 条` }} />
        </Card>
    );
};

export const UserCell = ({ r }) => {
    const u = r.user || {};
    const id = r.user_id ?? u.id;
    if (!id && !u.email && !r.user_email) return '-';
    return <span>{id ? `#${id} ` : ''}{u.username || r.username || ''}<br /><span style={{ color: '#999', fontSize: 12 }}>{u.email || r.user_email || ''}</span></span>;
};
